/* eslint-disable max-lines */
import { store, EventBus, Events } from "jiffy-store";
import { initMixpanelEvents, sendDataLayer, track } from "jiffy-analytics";
import { on as onEvent } from "common/utils/ui/event";
import api from "common/utils/api";
import { objNamesToCamelCase } from "common/utils/helpers/cases";
import deliveryDateClass from "common/utils/delivery-date/delivery-date-class";
import deliveryDateFormClass from "common/utils/delivery-date/delivery-date-form-class";
import deliveryLocationClass from "common/utils/delivery-location/delivery-location-class";
import { CachedDeliveryManager } from "common/utils/delivery-date/cached-delivery-manager";
import { notifyError } from "common/utils/app-monitoring/notify";
import initMadeApparelBenefits from "components/made_apparel_benefits/component";
import initColorPicker from "components/jiffy/product-color-picker/product-color-picker";
import initProductsGrid from "components/jiffy/product-card-v3/products-grid";
import initBrandsLinkTracking from "./brands/brands";
import "./faq/jiffymade-landing-faq";
import "./ui";
import { loadReactReorderModal, loadReviews, loadGallery } from "./lazy-components";

// Ensure PDP slice exists for components like bulk-discount-slider that rely on it
if (!store.pdp) {
  store.register("pdp", {
    originalCartTotal: 0,
    activeCart: null,
    loadedPrices: null
  });
}
const $getCompetingSection = () => document.querySelector('[data-section="competing"]');
const $getMadeCategoriesSection = () => document.querySelector(".js-made-categories-section");
const $getFeaturedDesignStartButton = () =>
  $getMadeCategoriesSection()?.querySelector(".js-start-designing");
const buildLineItemProperties = lineItem => ({
  product_id: lineItem?.item?.id,
  variant_id: lineItem?.item?.variantId,
  product_name: lineItem?.productName,
  quantity: lineItem?.qty,
  brand: lineItem?.brand?.name
});

const buildProjectProperties = (projectUuid, projectName, projectId) => ({
  project_uuid: projectUuid,
  project_name: projectName,
  project_id: projectId
});

const trackClick = ({
  lineItem,
  eventName,
  gaLabel,
  pageLocation,
  businessLine,
  sectionIdentifier,
  eventCategory,
  eventAction,
  projectUuid,
  projectName,
  projectId
}) => {
  const trackProperties = {
    eventCategory,
    page_location: pageLocation,
    business_line: businessLine,
    section_identifier: sectionIdentifier,
    ...(lineItem && buildLineItemProperties(lineItem)),
    ...(projectUuid && buildProjectProperties(projectUuid, projectName, projectId))
  };

  track(eventName, trackProperties);
  sendDataLayer({
    genericEvent: true,
    eventCategory,
    event: eventAction,
    eventLabel: gaLabel,
    page_location: pageLocation,
    business_line: businessLine,
    section_identifier: sectionIdentifier
  });
};

const initTopBannerClickTracking = () => {
  onEvent("click", ".js-header-promo-banner", () => {
    trackClick({
      eventName: "start_designing_clicked",
      pageLocation: "new_category_page",
      businessLine: "print",
      sectionIdentifier: "header_banner",
      eventCategory: "start_designing_clicked",
      eventAction: "click"
    });
  });
};

const initStartDesigningButtons = () => {
  onEvent("click", ".js-start-designing-trigger", () => {
    $getFeaturedDesignStartButton()?.click();
  });
};

const checkZipcodeChanges = async (zipcode, oldZipcode) => {
  try {
    const data = await api.made.categorySections.checkChanges({
      zipcode,
      old_zipcode: oldZipcode
    });

    if (data.needs_reload) {
      window.location.reload();
      return;
    }

    $getCompetingSection()?.classList.remove("pointer-events-none", "opacity-50");
  } catch (error) {
    $getCompetingSection()?.classList.remove("pointer-events-none", "opacity-50");
  }
};

/**
 * Handles cached delivery updates for the new delivery section
 */
const handleNewDeliverySectionUpdate = async () => {
  const CONTAINER_ID = "js-delivery-section-new-look-container";
  const $container = document.getElementById(CONTAINER_ID);

  if (!$container) return;

  // Check if this container supports cached delivery
  const hasCachedDelivery = $container.dataset.hasCachedDelivery === "true";
  if (!hasCachedDelivery) return;

  try {
    // Try to get fresh cached delivery date for the new zipcode
    const updated = await CachedDeliveryManager.updateCachedDeliveryDate();

    if (updated) {
      // Apply the updated cached delivery date
      const $deliverySpan = $container.querySelector(".text-foreground span:first-child");
      if ($deliverySpan && window.jiffyCachedDeliveryData?.deliveryDate) {
        $deliverySpan.textContent = window.jiffyCachedDeliveryData?.deliveryDate;
      }
    }
  } catch (error) {
    // Silently fail - cached delivery is a performance optimization
  }
};

const handleZipcodeChangeAfterLocationUpdate = async (zipcode, oldZipcode) => {
  $getCompetingSection()?.classList.add("pointer-events-none", "opacity-50");

  try {
    await checkZipcodeChanges(zipcode, oldZipcode);
    // Handle cached delivery update for new delivery section
    await handleNewDeliverySectionUpdate();
  } catch (error) {
    $getCompetingSection()?.classList.remove("pointer-events-none", "opacity-50");
  }
};

const handleReorderButtonClick = (button, event) => {
  event.stopPropagation(); // Stop js-favorite-card-link click
  try {
    const lineItem = objNamesToCamelCase(JSON.parse(button.dataset.lineItem));
    const { gaLabel } = button.dataset;
    trackClick({
      lineItem,
      eventName: "made_category_reorder_clicked",
      gaLabel,
      pageLocation: "new_category_page",
      businessLine: "print",
      sectionIdentifier: "past_orders",
      eventCategory: "productclick",
      eventAction: "click"
    });

    EventBus.publish(Events.OPEN_MADE_PRODUCT_UPSERT_MODAL, {
      lineItem
    });
  } catch (error) {
    notifyError("Failed to open reorder modal", {
      params: {
        error: JSON.stringify(error)
      }
    });
  }
};

const loadUserSection = async endpoint => {
  const url = `/account/made_category_user_section/${endpoint}`;
  try {
    const response = await fetch(url, {
      headers: {
        Accept: "text/javascript",
        "X-Requested-With": "XMLHttpRequest"
      },
      credentials: "same-origin"
    });

    if (!response.ok) {
      return;
    }

    const responseText = await response.text();
    if (responseText.trim().length === 0) {
      return;
    }

    const $script = document.createElement("script");
    $script.textContent = responseText;
    document.body.appendChild($script);
    document.body.removeChild($script);
  } catch (error) {
    notifyError("Failed to load user section", {
      params: { endpoint, error: error.message }
    });
  }
};

const loadUserSections = async () => {
  const $favoritesContainer = document.getElementById("js-user-favorites-container");
  const $savedProjectsContainer = document.getElementById("js-user-saved-projects-container");
  const $orderedItemsContainer = document.getElementById("js-user-ordered-items-container");

  if (!$favoritesContainer && !$savedProjectsContainer && !$orderedItemsContainer) {
    return;
  }

  // Only load sections if containers have skeleton content (user is signed in)
  if ($favoritesContainer && $favoritesContainer.children.length > 0) {
    await loadUserSection("user_favorites");
  }

  if ($savedProjectsContainer && $savedProjectsContainer.children.length > 0) {
    await loadUserSection("user_saved_projects");
  }

  if ($orderedItemsContainer && $orderedItemsContainer.children.length > 0) {
    await loadUserSection("user_ordered_items");
  }
};

// Track if event delegation is already set up
let reorderEventDelegationInitialized = false;
// Store reference to enableBuyItAgain callback for EventBus cleanup
let enableBuyItAgainCallback = null;

const initReorderMethod = () => {
  const REORDER_BUTTON_SELECTOR = "[data-testid^='ordered-item-card'] .js-made-reorder-button";

  // Use event delegation (only set up once)
  if (!reorderEventDelegationInitialized) {
    onEvent("click", REORDER_BUTTON_SELECTOR, (event, target) => {
      handleReorderButtonClick(target, event);
    });
    reorderEventDelegationInitialized = true;
  }

  // Optimize button enabling with single query and simplified loop
  const enableBuyItAgain = () => {
    document.querySelectorAll(REORDER_BUTTON_SELECTOR).forEach(buttonElement => {
      const button = buttonElement;
      button.disabled = false;
    });
  };

  // Remove existing EventBus listener before adding a new one to prevent duplicates
  if (enableBuyItAgainCallback) {
    EventBus.removeEventListener(Events.SAVED_CARTS_LOADED, enableBuyItAgainCallback);
  }

  enableBuyItAgainCallback = enableBuyItAgain;
  EventBus.on(Events.SAVED_CARTS_LOADED, enableBuyItAgain);
  enableBuyItAgain();
};

const handlePageEvents = () => {
  EventBus.on(Events.ZIPCODE_UPDATED, (event, zipcode, oldZipcode) => {
    if (zipcode && oldZipcode && zipcode.length >= 5) {
      handleZipcodeChangeAfterLocationUpdate(zipcode, oldZipcode);
    }
  });
};
const init = () => {
  deliveryDateClass().init();
  deliveryDateFormClass().init(true);
  if (document.querySelector(".js-delivery-section-new-look")) {
    deliveryLocationClass();
  }
  onEvent("click", "[data-new-category-click-tracker]", event => {
    const $trackerElement = event.target.closest("[data-new-category-click-tracker]");
    trackClick(JSON.parse($trackerElement.dataset.newCategoryClickTracker));
  });
  initTopBannerClickTracking();
  initMadeApparelBenefits();
  loadReviews();
  loadGallery();
  initColorPicker();
  initProductsGrid();
  initStartDesigningButtons();
  initMixpanelEvents(".js-made-category");
  initBrandsLinkTracking();
  handlePageEvents();

  window.initReactMadeReorder = async () => {
    const initFn = await loadReactReorderModal();
    return initFn();
  };
  window.initReorderMethod = initReorderMethod;

  loadUserSections();
};

export default init;
